import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Clock, Phone, Wrench, Truck, Users, CalendarDays, HardHat } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const FIELD_ROLES = ['millwright', 'millwright_foreman', 'labourer', 'welder', 'apprentice', 'field_worker'];

const dayHeading = (date, today) => {
  const d = new Date(`${date}T00:00:00`);
  const label = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  if (date === today) return `Today · ${label}`;
  const t = new Date(`${today}T00:00:00`);
  const diff = Math.round((d - t) / 86400000);
  if (diff === 1) return `Tomorrow · ${label}`;
  if (diff === -1) return `Yesterday · ${label}`;
  return label;
};

const mapsUrl = (place) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`;

function Assignment({ a }) {
  if (a.kind === 'off') {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`my-sched-${a.id}`}>
        <p className="text-sm text-slate-600"><span className="font-semibold text-slate-800">Off</span> — {a.customer}</p>
        {(a.crew_names || []).length > 0 && <p className="text-[11px] text-slate-500 text-right">also off: {a.crew_names.join(', ')}</p>}
      </div>
    );
  }
  return (
    <div className="bg-slate-50/80 border border-slate-200 rounded-lg p-3" data-testid={`my-sched-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-slate-900 font-bold text-sm leading-snug">
            {a.customer || 'Job'}{a.work_order ? <span className="font-mono text-sky-800 font-semibold ml-2">{a.work_order}</span> : null}
          </p>
          {a.description && <p className="text-xs text-slate-600 mt-0.5">{a.description}</p>}
        </div>
        {a.is_foreman && <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><HardHat className="w-3 h-3" /> Foreman</span>}
      </div>
      <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
        {a.start && <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> Start {a.start}</p>}
        {a.place && (
          <a href={mapsUrl(a.place)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sky-700 hover:text-sky-900 sm:col-span-2" data-testid={`my-sched-map-${a.id}`}>
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" /> <span className="underline decoration-dotted">{a.place}</span>
          </a>
        )}
        {a.contact && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" /> {a.contact}</p>}
        {a.tools && <p className="flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-slate-400" /> {a.tools}</p>}
        {(a.truck || a.trailer || a.welder || a.forklift) && (
          <p className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-slate-500" />
            {[a.truck && `Truck: ${a.truck}`, a.trailer && `Trailer: ${a.trailer}`, a.welder && `Welder: ${a.welder}`, a.forklift && `Forklift: ${a.forklift}`].filter(Boolean).join(' · ')}
          </p>
        )}
        {(a.crew_names || []).length > 0 && (
          <p className="flex items-start gap-1.5 sm:col-span-2"><Users className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
            <span>{!a.is_foreman && a.foreman_name ? <><span className="text-amber-800 font-semibold">{a.foreman_name}</span> (foreman){a.crew_names.filter(n => n !== a.foreman_name).length ? ', ' : ''}</> : null}
              {a.crew_names.filter(n => n !== a.foreman_name).join(', ')}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// Employee's own upcoming jobs pulled from Blair's dispatch sheet
export default function MySheetSchedule({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/manpower/my-schedule`, { ...auth(), params: { days: 14 } }).then(r => setData(r.data)).catch(() => setData({ assignments: [] }));
  }, []);
  if (!data) return null;
  const isField = FIELD_ROLES.includes(user?.role);
  if (!data.assignments?.length && !isField) return null;

  const byDate = {};
  for (const a of data.assignments || []) (byDate[a.date] = byDate[a.date] || []).push(a);
  const dates = Object.keys(byDate).sort();

  return (
    <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-5 mb-6" data-testid="my-sheet-schedule-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> My Schedule — where &amp; when</p>
        {data.last_sync_at && <p className="text-[10px] text-slate-500">from the dispatch sheet · {new Date(data.last_sync_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>}
      </div>
      {dates.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="my-sheet-schedule-empty">Nothing on the dispatch sheet for you in the next two weeks yet.</p>
      ) : (
        <div className="space-y-4">
          {dates.map(d => (
            <div key={d} data-testid={`my-sched-day-${d}`}>
              <p className={`text-xs font-bold uppercase tracking-wide mb-1.5 ${d === data.today ? 'text-emerald-700' : d < data.today ? 'text-slate-400' : 'text-sky-700'}`}>{dayHeading(d, data.today)}</p>
              <div className="space-y-2">{byDate[d].map(a => <Assignment key={a.id} a={a} />)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
